// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — the three modes.
//
// A mode has to change how the game PLAYS, not turn a difficulty dial, so each of these moves a
// different axis and each one INVALIDATES the previous mode's instincts:
//
//   TRUNK  goal: join everything. Vents pay when they SEAL (their network holds another vent), and
//          every network pays superlinearly for the vents inside it. Loops are free and good — they
//          cost nothing and close two open ends at once. The coupler is the crown jewel: the only
//          bridge between the six culvert vents and the six filament vents.
//
//   FLUME  goal: INVERTED. Vents pay nothing whatsoever. Score is length — 3 a cell for each gauge's
//          longest SIMPLE path — and every same-gauge cell NOT on that path is charged. So a loop is
//          now pure waste, a tee cannot lengthen a simple path and is near-dead weight, and the
//          coupler is a TRAP that cuts a long run in two. You grow one snake, not a web.
//
//   SEEP   COMMITMENT. Fittings arrive ONE AT A TIME — no lookahead, no ordering, you place what you
//          are given — and to make that survivable a mismatched join becomes legal at a price
//          instead of refused. Measured, the reveal is the axis that carries the mode: opening the
//          hand from one fitting to four moves the strong bot from 29.3 to 39.9. The relaxed
//          legality is a SAFETY VALVE rather than a strategic lever, and the sim is unambiguous
//          about that — a weep does not conduct, so it costs you the connection as well as the
//          points, and at every price tested (-1, -2, -3, -4) a bot allowed to weep beat a bot that
//          refused to by at most 1.7 points, and at cheap prices lost badly by over-using it. It is
//          there so that blind commitment can never strand you, not so you can trade points for
//          shape.
//
// THE OFF-PATH CHARGE IN FLUME EXISTS BECAUSE THE MODE OTHERWISE HAS NO DECISIONS IN IT. Without it
// the marginal value of extending the snake is +3 every single time and nothing competes, so a
// one-ply greedy bot is near-optimal and the mode is Trunk with an inverted sign. tests/balance.test
// carries a DEPTH gate — the strong bot must beat the greedy bot by a wide margin in EVERY mode —
// precisely so that a mode with a closed-form optimal policy fails loudly instead of shipping.
//
// The HOST's pick is what the room plays and it travels frozen inside rematch.ts's round start.
// `modeOf` guards an id off the wire with Object.hasOwn: a bare `MODES[id] || DEFAULT` lets
// 'constructor' through as a Mode of undefined fields, which reaches the dealer as `undefined`.

export type ModeId = 'trunk' | 'flume' | 'seep';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** How many rounds of four fittings. */
  readonly rounds: number;
  /** Do vents score at all? False in Flume, which is the whole inversion. */
  readonly ventsScore: boolean;
  /** Points for sealing a vent: [first at the table, later]. */
  readonly claim: readonly [number, number];
  /** NET payout per network holding k vents, summed over every network. A table so it is sweepable. */
  readonly net: readonly number[];
  /** Points per cell of each gauge's longest simple path. */
  readonly runPer: number;
  /** Charge per same-gauge cell NOT on that gauge's scored path. Flume only. */
  readonly offPath: number;
  /** Charge per open live side facing an empty cell at the finish. */
  readonly ends: number;
  /** Mismatched joins legal-but-charged, and non-conducting. Seep only. */
  readonly relaxed: boolean;
  /** Charge per weeping half-edge. */
  readonly weep: number;
  /** Charge for a scrapped fitting when genuinely nothing fits. */
  readonly scrap: number;
  /** How many of the round's four fittings are visible up front. 4 = full lookahead, 1 = none. */
  readonly reveal: number;
}

/**
 * NET pays 2k(k-1) for every network holding k vents, summed over ALL of them:
 * a pair -> 4, a trio -> 12, four -> 24, five -> 40.
 *
 * Superlinear so that joining two pairs into a four (4 + 4 = 8 becomes 24) is worth three times what
 * you started with — that is the whole "consolidate rather than sprawl" pressure, and a linear
 * payout would make a second network exactly as good as extending the first.
 *
 * IT PAYS OVER EVERY NETWORK, NOT JUST THE BIGGEST, AND THAT WAS A MEASURED CORRECTION. The first
 * draft paid for the largest component only. A sweep that QUADRUPLED that payout produced
 * byte-identical play — same component sizes, same component count, same everything, on every seed —
 * because a largest-only term is a lump sum that moves solely at the instant two networks merge, so
 * for almost every candidate placement it adds the same constant to every option and drops out of
 * the ranking. Paying per network makes each vent you add worth something the moment you add it.
 *
 * Capped at k=8 so one lucky network cannot outweigh an entire average run.
 */
const NET_TABLE: readonly number[] = [0, 0, 4, 12, 24, 40, 60, 84, 112, 112, 112, 112, 112];

export const MODES: Record<ModeId, Mode> = {
  trunk: {
    id: 'trunk',
    name: 'Trunk',
    blurb: 'Gather the edge into networks. Loops are free; loose ends are not.',
    rounds: 7,
    ventsScore: true,
    claim: [5, 2],
    net: NET_TABLE,
    runPer: 1,
    offPath: 0,
    ends: -2,
    relaxed: false,
    weep: 0,
    scrap: -2,
    reveal: 4,
  },
  flume: {
    id: 'flume',
    name: 'Flume',
    blurb: 'Vents pay nothing. Grow one long unbroken run of each gauge — every stray cell costs.',
    rounds: 7,
    ventsScore: false,
    claim: [0, 0],
    net: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    runPer: 3,
    offPath: -1,
    ends: -1,
    relaxed: false,
    weep: 0,
    scrap: -2,
    reveal: 4,
  },
  seep: {
    id: 'seep',
    name: 'Seep',
    blurb: 'One fitting at a time, no looking ahead. Anything fits — badly — so you are never stuck.',
    rounds: 7,
    ventsScore: true,
    claim: [5, 2],
    net: NET_TABLE,
    runPer: 1,
    offPath: 0,
    ends: -2,
    relaxed: true,
    weep: -4,
    scrap: -2,
    reveal: 1,
  },
};

export const MODE_IDS: readonly ModeId[] = ['trunk', 'flume', 'seep'];
export const DEFAULT_MODE: ModeId = 'trunk';

/** Guard an id that arrived off the wire or out of localStorage. Never index MODES directly. */
export function modeOf(id: unknown): Mode {
  return typeof id === 'string' && Object.hasOwn(MODES, id) ? MODES[id as ModeId] : MODES[DEFAULT_MODE];
}

/** Fittings per round. Fixed across every mode — the round count is the length lever. */
export const PER_ROUND = 4;

export const marksIn = (m: Mode): number => m.rounds * PER_ROUND;
